<template>
    <accordion
        class="drs-shop"
        header-class="drs-shop__header"
        :class="{ selected: shop.selected }"
        :is-active="expandedShopId === shop.id"
    >
        <template #header>
            <ui-checkbox-input
                readonly
                class="text-sm"
                :name="`shop-${shop.id}`"
                :label="`${shop.full_name}, ${shop.fullAddress}`"
                :value="shop.selected"
            />
        </template>

        <template #expand-button>
            <div class="text-md cursor-pointer" @click="toggleShopExpand">
                <a-icon class="pr-1" :icon="expandedShopId === shop.id ? 'ArrowUp' : 'ArrowDown'" />
                <strong v-text="'Konfiguruj'" />
            </div>
        </template>

        <template #body>
            <div class="select-devices">
                <div class="select-devices__row --header">
                    <div v-text="'Urządzenie'" />
                    <div v-text="'Liczba'" />
                    <div />
                </div>

                <select-device
                    v-for="(device, idx) in shopDevices"
                    class="select-devices__row"
                    :key="idx"
                    :additional-devices="!!shop.additionalDevices"
                    :device="device"
                    :device-options="availableDeviceTypes"
                    :qty-left="qtyLeft"
                    :shop-devices-length="shopDevices.length"
                    @remove="removeDevice(idx)"
                    @update:device="updateDevice(idx, $event)"
                />

                <the-btn
                    class="btn-secondary align-self-start"
                    text="Dodaj urządzenie"
                    :disabled="!canAddDeviceType"
                    @click="addDeviceTypesRow"
                />
            </div>

            <radio-input
                id="dealer-selection-auto"
                name="dealer-selection"
                class="mb-2"
                :data="{
                    label: $options.dealerSelectionsLabels[$options.dealerSelections.AUTO],
                    value: $options.dealerSelections.AUTO
                }"
                :value="shop.dealerSelection"
                @change="changeDealerSelection"
            />
            <radio-input
                id="dealer-selection-manual"
                name="dealer-selection"
                class="mb-2"
                :data="{
                    label: $options.dealerSelectionsLabels[$options.dealerSelections.MANUAL],
                    value: $options.dealerSelections.MANUAL
                }"
                :value="shop.dealerSelection"
                @change="changeDealerSelection"
            />

            <template v-if="shop.dealerSelection === $options.dealerSelections.MANUAL">
                <h6 class="my-3">
                    Opis
                    <a-icon
                        v-tooltip="`Wpisz w polu tekstowym takie informacje jak preferowany dealer<br/>
                            oraz ewentualnie imię i nazwisko serwisanta. Możesz też opcjonalnie<br/>
                            wprowadzić numer telefonu do kontaktu z dealerem. Jeśli go nie wpiszesz,<br/>
                            serwisant zadzwoni na numer podany jako telefon kontaktowy w EuroPlatform`"
                        icon="Info"
                        width="16"
                        height="16"
                        style="position: relative; top: 2px"
                    />
                </h6>
                <the-text-area
                    required
                    label="Podaj nazwę preferowanego serwisu kas fiskalnych oraz opcjonalnie imię i nazwisko serwisanta"
                    :value="dealerText"
                    :maxlength="500"
                    @input="dealerText = $event"
                />
                <the-input
                    label="NIP serwisu"
                    :maxlength="10"
                    :text="dealerNip"
                    :style="{ width: '300px', maxWidth: '100%' }"
                    @input="dealerNip = $event"
                />
            </template>
        </template>
    </accordion>
</template>

<script>
import { mapGetters } from 'vuex'
import { DEALER_SELECTIONS, DEALER_SELECTIONS_LABELS } from '@/utils/constants/drs/dealer-selections'
import { DEVICE_TYPES } from '@/utils/constants/drs/device-types'
import cloneDumb from '@/helpers/cloneDumb'
import SelectDevice from '@/components/drs/configuration/SelectDevice.vue'

const MAX_QTY = 100

export default {
    dealerSelections: DEALER_SELECTIONS,

    dealerSelectionsLabels: DEALER_SELECTIONS_LABELS,

    deviceTypes: DEVICE_TYPES,

    props: {
        shop: {
            type: Object,
            required: true
        },
        expandedShopId: {
            type: [Number, null],
            required: false
        },
        tariff: {
            type: Object,
            required: true
        }
    },

    computed: {
        ...mapGetters('drsTariffs', ['selectedTariff']),
        ...mapGetters('drsIntegrations', ['integrationsCount']),

        dealerText: {
            get() {
                return this.shop.dealerText || ''
            },
            set(value) {
                const tariff = cloneDumb(this.selectedTariff)

                const location = tariff.locations.find(el => el.id === this.shop.id && el.selected)
                this.$set(location, 'dealerText', value)

                this.$store.commit('drsTariffs/UPDATE_TARIFFS', tariff)
            }
        },

        dealerNip: {
            get() {
                return this.shop.dealerNip || ''
            },
            set(value) {
                const tariff = cloneDumb(this.selectedTariff)
                const location = tariff.locations.find(el => el.id === this.shop.id && el.selected)
                this.$set(location, 'dealerNip', value)
                this.$store.commit('drsTariffs/UPDATE_TARIFFS', tariff)
            }
        },

        shopDevices() {
            return this.shop.deviceTypes || []
        },

        availableDeviceTypes() {
            return DEVICE_TYPES.map(dt => ({
                label: dt,
                value: dt,
                disabled: this.shopDevices.some(sdt => sdt.type === dt)
            }))
        },

        devicesQuantity() {
            // Calculate the quantity of devices
            return this.shopDevices
                .filter(device => device.type !== null)
                .reduce((acc, device) => acc + device.qty, 0)
        },

        canAddDeviceType() {
            if (!this.shop.additionalDevices) {
                return false
            }

            if (this.qtyLeft === 0) {
                return false
            }

            return true
        },

        maxQty() {
            if (!this.shop.additionalDevices) {
                return 1
            }

            return this.tariff.optionals.list.find(el => el.code === 'ecr')?.maxQty || MAX_QTY
        },

        qtyLeft() {
            return this.maxQty - this.devicesQuantity - this.integrationsCount
        }
    },

    methods: {
        toggleShopExpand() {
            this.$emit('expand-shop', this.shop.id)
        },

        changeDealerSelection(value) {
            const tariff = cloneDumb(this.selectedTariff)
            const location = tariff.locations.find(el => el.id === this.shop.id && el.selected)
            this.$set(location, 'dealerSelection', value)
            this.$store.commit('drsTariffs/UPDATE_TARIFFS', tariff)
        },

        addDeviceTypesRow() {
            if (!this.canAddDeviceType) {
                return
            }

            const tariff = cloneDumb(this.selectedTariff)
            const location = tariff.locations.find(el => el.id === this.shop.id && el.selected)

            if (!location?.deviceTypes) {
                // If deviceTypes array doesn't exist, create it
                this.$set(location, 'deviceTypes', [])
            }

            // Add a new row
            location.deviceTypes.push({
                type: null,
                qty: 0
            })

            this.$store.commit('drsTariffs/UPDATE_TARIFFS', tariff)
        },

        updateDevice(index, value) {
            const tariff = cloneDumb(this.selectedTariff)

            const location = tariff.locations.find(el => el.id === this.shop.id && el.selected)

            location.deviceTypes[index] = value

            this.$store.commit('drsTariffs/UPDATE_TARIFFS', tariff)
        },

        // When user doesn't want additional devices, reset device types
        resetDeviceTypes() {
            if (this.shop.additionalDevices || this.shop.deviceTypes) {
                return
            }

            this.shop.deviceTypes = [{
                type: null,
                qty: 0
            }]
        },

        removeDevice(index) {
            const tariff = cloneDumb(this.selectedTariff)
            const location = tariff.locations.find(el => el.id === this.shop.id && el.selected)

            if (!location?.deviceTypes?.[index]) {
                return
            }

            location.deviceTypes.splice(index, 1)

            this.$store.commit('drsTariffs/UPDATE_TARIFFS', tariff)
        }
    },

    created() {
        this.resetDeviceTypes()

        if (!this.shopDevices.length) {
            this.addDeviceTypesRow()
        }

        if (typeof this.shop.dealerSelection === 'undefined') {
            this.changeDealerSelection(DEALER_SELECTIONS.MANUAL)
        }
    },

    components: {
        SelectDevice
    }
}
</script>

<style lang="scss">
.drs-shop {
    --padding: 1.25rem;

    .drs-shop__header {
        padding: var(--padding);
    }

    &.selected {
        background-color: var(--active-promo-bg);
        border: 2px solid var(--color-primary);

        &:hover {
            border-width: 2px;
        }

        .drs-shop__header {
            padding: calc(var(--padding) - 1px);
        }
    }
}

.drs-shop-table {
    border-collapse: collapse;
    width: 100%;

    &__row {
        display: flex;
        gap: 0.5rem;

        + .drs-shop-table__row {
            margin-top: 0.75rem;
        }

        &.--header {
            text-transform: uppercase;
            letter-spacing: 0.08rem;
            font-size: 10px;
            margin-bottom: 1rem;
        }

        &.--optional {
            align-items: center;
        }

        > :nth-child(1) {
            width: 100%;
        }
        > :nth-child(2) {
            width: 6rem;
            min-width: 6rem;
        }
        > :nth-child(3) {
            width: 6rem;
            min-width: 6rem;
        }
    }
}

.select-devices {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
    margin-bottom: 2rem;

    &__row {
        --col-width: 50%;
        display: flex;
        justify-content: space-between;
        align-items: start;
        gap: 0.5rem;
        width: 100%;

        > * {
            width: var(--col-width);
            min-width: var(--col-width);
        }

        > :nth-child(2) {
            --col-width: 6rem;
            margin-left: auto;
        }

        > :nth-child(3) {
            --col-width: 3.5rem;
        }

        &.--header {
            text-transform: uppercase;
            letter-spacing: 0.08rem;
            font-size: 10px;
            margin-bottom: .5rem;
        }
    }

    @media (max-width: 600px) {
        &__row {
            > :nth-child(2) {
                --col-width: 4rem;
            }
        }
    }
}
</style>